import React from 'react'
import { Link } from 'react-router-dom'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../ui/card'
import { Button } from '../../../ui/button'
import { Alert, AlertDescription, AlertTitle } from '../../../ui/alert'
import {
  UserPlus,
  ExternalLink,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  Loader2,
} from 'lucide-react'
import { Event as EventModel } from '../../../../model/Event'
import { SetupStepState, setupStepHref } from '../eventSetupStatus'

interface StaffStepProps {
  event: EventModel
  step: SetupStepState
  rolesDone: boolean
  canProvision: boolean
  loading: boolean
  onRefresh: () => void
}

/**
 * Hand-off to Quick Provisioning. The provisioning grid is a page of its own —
 * embedding it here would fork a 900-line component — so this step explains
 * what to do there and then confirms it worked.
 */
export const StaffStep: React.FC<StaffStepProps> = ({
  event,
  step,
  rolesDone,
  canProvision,
  loading,
  onRefresh,
}) => (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2 text-lg">
        <UserPlus className="h-4 w-4" />
        Provision your staff
      </CardTitle>
      <CardDescription>{step.help}</CardDescription>
    </CardHeader>
    <CardContent className="space-y-4">
      {!rolesDone && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Add a schedulable role first</AlertTitle>
          <AlertDescription>
            People are provisioned into a role. Until this event has a role marked
            schedulable, anyone you provision will not appear on the schedule.
          </AlertDescription>
        </Alert>
      )}

      <ol className="list-decimal space-y-2 rounded-md border p-4 pl-8 text-sm text-muted-foreground">
        <li>
          Open Quick Provisioning — it lists every identity available to this
          event, with one column per role.
        </li>
        <li>
          Click the cell where a person meets the role you want them in. Roles
          marked with a calendar icon are schedulable.
        </li>
        <li>
          Use <span className="font-medium text-foreground">Assign All Unassigned</span>{' '}
          to put everyone left over into one role in a single step.
        </li>
        <li>
          Missing somebody? Use{' '}
          <span className="font-medium text-foreground">New Identity</span> to create
          them without leaving the page.
        </li>
        <li>Come back here and refresh to confirm the count.</li>
      </ol>

      <div className="flex flex-wrap items-center gap-3">
        {canProvision ? (
          <Button asChild>
            <Link to={setupStepHref('staff', event.id)}>
              <ExternalLink className="mr-2 h-4 w-4" />
              Open Quick Provisioning
            </Link>
          </Button>
        ) : (
          <p className="text-sm text-muted-foreground">
            You do not have permission to provision people for this event — ask an
            identity manager to complete this step.
          </p>
        )}
        <Button variant="outline" onClick={onRefresh} disabled={loading}>
          {loading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Refresh count
        </Button>
      </div>

      <div className="flex items-center gap-2 rounded-md border px-3 py-2.5 text-sm">
        {step.done ? (
          <CheckCircle2 className="h-4 w-4 text-green-600" />
        ) : (
          <AlertTriangle className="h-4 w-4 text-muted-foreground" />
        )}
        <span className="font-medium">
          {step.status === 'unknown'
            ? 'Checking who is available to schedule...'
            : step.done
              ? `${step.detail} ready to schedule`
              : 'Nobody is available to schedule yet'}
        </span>
      </div>
    </CardContent>
  </Card>
)
